import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * BigWater status pill — eyebrow chip in the brand DNA.
 * Variants map 1:1 to the landing's `.eyebrow-*` tokens.
 */
export type BadgeVariant = 'available' | 'completed' | 'locked' | 'points' | 'new' | 'soon' | 'rejected';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  /** Optional leading icon (lucide). Sized to match the chip. */
  icon?: ReactNode;
  /** Animated dot before the label — used for "live" availability. */
  pulse?: boolean;
}

const VARIANT_CLASS: Record<BadgeVariant, string> = {
  available: 'eyebrow eyebrow-water',
  completed: 'eyebrow eyebrow-forest',
  locked: 'eyebrow eyebrow-locked',
  points: 'eyebrow eyebrow-amber',
  new: 'eyebrow eyebrow-rose',
  soon: 'eyebrow eyebrow-amber',
  rejected: 'eyebrow eyebrow-rose',
};

const PULSE_DOT_CLASS: Partial<Record<BadgeVariant, string>> = {
  available: 'bg-water-500',
  new: 'bg-rose2-500',
  completed: 'bg-forest-500',
  rejected: 'bg-rose2-500',
};

export function Badge({
  variant = 'available',
  icon,
  pulse = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  const dotColor = PULSE_DOT_CLASS[variant] ?? 'bg-water-500';

  return (
    <span className={cn(VARIANT_CLASS[variant], className)} {...rest}>
      {pulse ? (
        <span aria-hidden="true" className="relative inline-flex h-1.5 w-1.5">
          <span
            className={cn(
              'absolute inline-flex h-full w-full rounded-full opacity-70 motion-safe:animate-ping',
              dotColor,
            )}
          />
          <span className={cn('relative inline-flex h-1.5 w-1.5 rounded-full', dotColor)} />
        </span>
      ) : null}
      {icon}
      {variant === 'points' ? <span className="font-mono">{children}</span> : children}
    </span>
  );
}

export default Badge;
